<template>
  <div class="structure-element-detail-page" style="margin-top: 8px">
    <nav class="entity-path" aria-label="breadcrumb">
      <div v-if="pathLoading" class="entity-path__loading">Loading…</div>
      <div v-else-if="pathError" class="entity-path__error">Couldn’t load path</div>
      <div v-else-if="pathSegments.length" class="entity-path__inner">
        <span v-for="(seg, idx) in pathSegments" :key="`${seg.type}-${seg.id}`">
          <span class="entity-path__label">{{ seg.label }}:</span>
          <router-link :to="seg.route">{{ seg.name }}</router-link>
          <span v-if="idx < pathSegments.length - 1" class="entity-path__sep"> / </span>
        </span>
      </div>
    </nav>

    <EntityOverviewLayout
      v-if="element"
      :leftPanelTabs="overviewLeftPanels"
      :defaultLeftPanel="defaultLeftPanel"
      :panelTabs="overviewPanels"
      defaultPanel="files"
    >
      <template #top-left>
        <div class="element-details">
        <div class="header">
          <h2>{{ isEditMode ? `Edit ${typeName}` : element.name }}</h2>
          <div class="top-buttons">
            <button @click="toggleEditMode" class="icon-btn">
              <i class="bi bi-pencil"></i>
            </button>
            <button class="remove-btn" @click="deleteElement">
              <i class="bi bi-trash"></i>
            </button>
          </div>
        </div>

        <div v-if="!isEditMode">
          <div class="detail"><strong>Name:</strong> {{ element.name || '-' }}</div>
          <div class="detail"><strong>Type:</strong> {{ typeName }}</div>
          <div class="detail">
            <strong>Project:</strong>
            <router-link
              v-if="element.project_id"
              class="linked-value"
              :to="{ name: 'ProjectDetailPage', params: { id: element.project_id } }"
            >
              {{ element.project_name || (`#${element.project_id}`) }}
            </router-link>
            <span v-else>{{ element.project_name || '-' }}</span>
          </div>
          <div class="detail"><strong>Parent:</strong> {{ element.parent_id == null ? 'Project' : (element.parent_name || ('#' + element.parent_id)) }}</div>
          <div class="detail"><strong>Status:</strong> {{ element.status || '-' }}</div>
          <div class="detail" v-if="requiresLocation">
            <strong>Location:</strong>
            <router-link
              v-if="element.location_id"
              class="linked-value"
              :to="{ name: 'LocationDetailPage', params: { id: element.location_id } }"
            >
              {{ locationName || (`#${element.location_id}`) }}
            </router-link>
            <span v-else>{{ locationName || '-' }}</span>
          </div>
          <div class="detail" v-if="isOrdered"><strong>Ordered:</strong> Yes</div>
        </div>

        <div v-else>
          <div class="form-group">
            <label for="element-name">Name</label>
            <input id="element-name" v-model.trim="editableElement.name" type="text" class="form-control" required />
          </div>

          <div v-if="requiresLocation" class="form-group">
            <label for="element-location">Location</label>
            <select id="element-location" v-model="editableElement.location_id" class="form-control" required>
              <option disabled value="">Select a Location</option>
              <option v-for="location in locations" :key="location.id" :value="location.id">
                {{ location.name }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label for="element-status">Status</label>
            <select id="element-status" v-model="editableElement.status" class="form-control">
              <option value="To be Started">To be Started</option>
              <option value="Ongoing">Ongoing</option>
              <option value="Finished">Finished</option>
            </select>
          </div>

          <div class="buttons-container">
            <button @click="saveChanges">Save</button>
            <button @click="cancelEditMode">Cancel</button>
          </div>
        </div>
        </div>
      </template>

      <template #top-right>
        <DisplayImageGallery entityType="structure_element" :entityId="elementIdNumeric" />
      </template>

      <template #bottom-left>
        <ExperimentList
          v-if="element && canHaveExperiments"
          :experiments="experiments"
          :projectId="projectIdNumeric"
          :projectName="element?.project_name || ''"
          :locationId="Number(element?.location_id) || null"
          :structureElementId="elementIdNumeric"
          :useCreateExperimentModal="true"
          @create-experiment="openCreateExperimentModal"
        />
      </template>

      <template #left-panel-experiments>
        <ExperimentList
          v-if="element && canHaveExperiments"
          :experiments="experiments"
          :projectId="projectIdNumeric"
          :projectName="element?.project_name || ''"
          :locationId="Number(element?.location_id) || null"
          :structureElementId="elementIdNumeric"
          :useCreateExperimentModal="true"
          @create-experiment="openCreateExperimentModal"
        />
      </template>

      <template #left-panel-children>
        <ProjectChildElementsList
          v-if="element && hasElementChildrenCapability"
          :elements="childElements"
          :projectId="projectIdNumeric"
          :structureSetId="structureSetIdForRequests"
          :projectName="element?.project_name || ''"
          :sectionTitle="childElementsSectionTitle"
          :parentElementId="elementIdNumeric"
          :isOrderedList="Boolean(rootAllowedChildTypes?.[0]?.is_ordered)"
          @refresh-elements="fetchChildElements"
        />
      </template>

      <template #panel-files>
        <ExperimentFiles
          ref="filesComp"
          :files="files"
          :folders="folders"
          :base-path="filesBasePath"
          :loadingTree="isFilesTreeLoading"
          :experimentId="element.id"
          :assId="element.id"
          :assType="'structure_element'"
          :isUploading="isUploading"
          :uploadPercent="uploadPercent"
          :uploadLabel="uploadLabel"
          @addFiles="addFiles"
          @removeFile="removeFile"
        />
      </template>

      <template #panel-comments>
        <ExperimentComments
          :comments="comments"
          :users="users"
          :files="files"
          :entityId="elementIdNumeric"
          @addComment="addComment"
          @removeComment="removeComment"
          @updateComment="handleUpdateComment"
        />
      </template>
    </EntityOverviewLayout>

    <FileUploadSession
      :visible="uploadSession.visible"
      :fileType="uploadSession.fileType"
      :entityId="uploadSession.entityId"
      :files="uploadSession.files"
      :entraId="loggedInUser?.entra_id"
      :folderId="uploadSession.folderId"
      @complete="handleUploadComplete"
      @close="handleUploadClose"
      @error="handleUploadError"
    />

    <HierarchyCreateExperimentModal
      v-if="showCreateExperimentModal"
      :project-name="element?.project_name || projectNameFromPath"
      :project-id="projectIdNumeric || 0"
      :context-label="'Parent Element'"
      :context-name="element?.name || ''"
      :form="newExperiment"
      :errors="createExperimentErrors"
      :locations="locations"
      :lock-location="Boolean(element?.location_id)"
      :fixed-location-name="locationName || ''"
      :is-submitting="isCreatingExperiment"
      @close="closeCreateExperimentModal"
      @update-field="updateNewExperimentField"
      @form-submit="submitCreateExperiment"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { parseFilesTree } from '@/utils/filesTree';
import { mapState } from 'vuex';
import { useToast } from 'vue-toastification';
import { confirmDelete } from '@/utils/confirmDelete';
import { API_ENDPOINTS } from '@/services/config';
import apiService from '@/services/apiService';
import EntityOverviewLayout from '@/components/EntityOverviewLayout.vue';
import DisplayImageGallery from '@/components/DisplayImageGallery.vue';
import ExperimentFiles from '@/components/ExperimentFiles.vue';
import ExperimentComments from '@/components/ExperimentComments.vue';
import ProjectChildElementsList from '@/components/ProjectChildElementsList.vue';
import ExperimentList from '@/components/ExperimentList.vue';
import FileUploadSession from '@/components/FileUploadSession.vue';
import HierarchyCreateExperimentModal from '@/components/HierarchyCreateExperimentModal.vue';

const buildDefaultExperiment = (projectId = null, structureElementId = null) => ({
  name: '',
  location_id: '',
  start_date: '',
  end_date: '',
  project_id: projectId ? Number(projectId) : '',
  project_structure_element_id: structureElementId ? Number(structureElementId) : '',
});

export default {
  name: 'StructureElementDetailPage',
  components: {
    EntityOverviewLayout,
    DisplayImageGallery,
    ExperimentFiles,
    ExperimentComments,
    ProjectChildElementsList,
    ExperimentList,
    FileUploadSession,
    HierarchyCreateExperimentModal,
  },
  data() {
    return {
      element: null,
      childElements: [],
      rootAllowedChildTypes: [],
      structureTypesById: {},
      structureTypesByCode: {},
      typeDefinition: null,
      isEditMode: false,
      editableElement: { name: '', location_id: '', status: 'To be Started' },
      locations: [],
      locationName: '',
      comments: [],
      users: [],
      experiments: [],
      showCreateExperimentModal: false,
      isCreatingExperiment: false,
      createExperimentErrors: [],
      newExperiment: buildDefaultExperiment(),
      files: [],
      folders: [],
      isFilesTreeLoading: false,
      filesBasePath: '',
      isUploading: false,
      uploadPercent: 0,
      uploadLabel: '',
      pathSegments: [],
      pathLoading: false,
      pathError: null,
      uploadSession: {
        visible: false,
        fileType: '',
        entityId: null,
        files: [],
        folderId: null,
      },
    };
  },
  computed: {
    ...mapState(['loggedInUser']),
    projectIdNumeric() {
      return this.element?.project_id || Number(this.$route.params.projectId) || null;
    },
    routeStructureSetId() {
      const raw = this.$route.query?.structure_set_id;
      if (raw == null || raw === '') {
        return null;
      }
      const numeric = Number(raw);
      return Number.isNaN(numeric) ? null : numeric;
    },
    structureSetIdForRequests() {
      const raw = this.element?.structure_set_id ?? this.element?.project_structure_set_id;
      if (raw != null && raw !== '') {
        return Number(raw);
      }
      return this.routeStructureSetId;
    },
    elementIdNumeric() {
      return Number(this.$route.params.elementId);
    },
    typeName() {
      return this.typeDefinition?.name || this.element?.type_name || 'Element';
    },
    projectNameFromPath() {
      const projectSegment = this.pathSegments.find((segment) => segment.type === 'project');
      return projectSegment?.name || '';
    },
    detailSectionTitle() {
      return `${this.typeName} Details`;
    },
    requiresLocation() {
      return !!this.typeDefinition?.requires_location;
    },
    isOrdered() {
      return !!this.typeDefinition?.is_ordered;
    },
    childElementsSectionTitle() {
      const firstType = this.childElements?.[0]?.type_name;
      if (!firstType) {
        const fallbackType = this.rootAllowedChildTypes?.[0]?.name;
        return fallbackType ? this.pluralizeTypeName(fallbackType) : 'Child Elements';
      }
      return this.pluralizeTypeName(firstType);
    },
    hasElementChildrenCapability() {
      return Array.isArray(this.rootAllowedChildTypes) && this.rootAllowedChildTypes.length > 0;
    },
    canHaveExperiments() {
      return !!this.typeDefinition?.is_experiment_parent;
    },
    overviewLeftPanels() {
      const panels = [];

      if (this.hasElementChildrenCapability) {
        panels.push({ id: 'children', label: this.childElementsSectionTitle });
      }

      if (this.canHaveExperiments) {
        panels.push({ id: 'experiments', label: 'Experiments' });
      }

      return panels;
    },
    defaultLeftPanel() {
      if (this.hasElementChildrenCapability) {
        return 'children';
      }
      if (this.canHaveExperiments) {
        return 'experiments';
      }
      return '';
    },
    overviewPanels() {
      return [
        { id: 'files', label: 'Files' },
        { id: 'comments', label: 'Comments' },
      ];
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  created() {
    this.bootstrap();
  },
  watch: {
    '$route.params.elementId': {
      async handler(newVal, oldVal) {
        if (newVal === oldVal) {
          return;
        }

        this.isEditMode = false;
        this.editableElement = { name: '', location_id: '' };
        this.element = null;
        this.childElements = [];
        this.rootAllowedChildTypes = [];
        this.locations = [];
        this.locationName = '';
        this.comments = [];
        this.experiments = [];
        this.showCreateExperimentModal = false;
        this.isCreatingExperiment = false;
        this.createExperimentErrors = [];
        this.newExperiment = buildDefaultExperiment();
        this.files = [];
        this.folders = [];

        await this.bootstrap();
      },
    },
  },
  methods: {
    async bootstrap() {
      await this.fetchStructureTypes();
      await this.fetchElement();
      await this.fetchPathSegments();
      await Promise.all([
        this.fetchChildElements(),
        this.fetchAllowedChildTypes(),
        this.fetchComments(),
        this.fetchUsers(),
        this.fetchExperiments(),
      ]);

      if (this.requiresLocation) {
        await Promise.all([this.fetchLocations(), this.fetchLocationName()]);
      }

      if (this.element) {
        await this.fetchFiles();
      }
    },

    normalizeCollection(payload, key) {
      if (Array.isArray(payload)) return payload;
      if (payload && Array.isArray(payload.items)) return payload.items;
      if (payload && Array.isArray(payload[key])) return payload[key];
      if (payload && payload.data && Array.isArray(payload.data.items)) return payload.data.items;
      if (payload && payload.data && Array.isArray(payload.data)) return payload.data;
      return [];
    },

    pluralizeTypeName(typeName) {
      const normalized = String(typeName || '').trim();
      if (!normalized) return 'Child Elements';
      const lower = normalized.toLowerCase();
      if (lower.endsWith('s')) return normalized;
      if (lower.endsWith('y') && !/[aeiou]y$/i.test(normalized)) return `${normalized.slice(0, -1)}ies`;
      return `${normalized}s`;
    },

    async fetchStructureTypes() {
      try {
        const response = await apiService.getProjectStructureTypes(this.loggedInUser.entra_id);
        const items = this.normalizeCollection(response, 'types');
        this.structureTypesById = items.reduce((acc, type) => {
          acc[type.id] = type;
          return acc;
        }, {});
        this.structureTypesByCode = items.reduce((acc, type) => {
          if (type.code) acc[type.code] = type;
          return acc;
        }, {});
      } catch (error) {
        this.structureTypesById = {};
        this.structureTypesByCode = {};
      }
    },

    async fetchElement() {
      try {
        const response = await apiService.getProjectStructureElement(
          this.elementIdNumeric,
          this.loggedInUser.entra_id,
          this.structureSetIdForRequests
        );
        const raw = response?.element || response?.data || response;
        const byId = raw?.type_id != null ? this.structureTypesById[raw.type_id] : null;
        const byCode = raw?.type_code ? this.structureTypesByCode[raw.type_code] : null;
        this.typeDefinition = byId || byCode || null;
        this.element = {
          ...raw,
          type_name: raw.type_name || this.typeDefinition?.name || '-',
          parent_name: raw.parent_name || (raw.parent_id == null ? 'Project' : null),
        };
        this.editableElement = {
          name: this.element.name || '',
          location_id: this.element.location_id != null ? Number(this.element.location_id) : '',
          status: this.element.status || 'To be Started',
        };
      } catch (error) {
        this.toast.error('Failed to fetch element details.');
      }
    },

    async fetchPathSegments() {
      if (!this.element) {
        this.pathSegments = [];
        return;
      }

      this.pathLoading = true;
      this.pathError = null;
      try {
        const response = await apiService.get(
          API_ENDPOINTS.path,
          { project_structure_element_id: this.elementIdNumeric },
          this.loggedInUser.entra_id
        );

        const items = Array.isArray(response)
          ? response
          : response?.items || response?.data?.items || [];

        let resolvedProjectId = Number(this.element?.project_id || this.projectIdNumeric) || null;
        const segments = [];

        items.forEach((item) => {
          if (item?.project?.id) {
            resolvedProjectId = Number(item.project.id) || resolvedProjectId;
            segments.push({
              type: 'project',
              id: item.project.id,
              label: 'Project',
              name: item.project.name || `#${item.project.id}`,
              route: { name: 'ProjectDetailPage', params: { id: item.project.id } },
            });
            return;
          }

          if (item?.element?.id) {
            segments.push({
              type: 'structure-element',
              id: item.element.id,
              label: item.type?.name || 'Element',
              name: item.element.name || `#${item.element.id}`,
              route: {
                name: 'StructureElementDetailPage',
                params: {
                  projectId: resolvedProjectId,
                  elementId: item.element.id,
                },
              },
            });
          }
        });

        this.pathSegments = segments;
      } catch (error) {
        this.pathSegments = [];
        this.pathError = error;
      } finally {
        this.pathLoading = false;
      }
    },

    async fetchAllowedChildTypes() {
      try {
        const response = await apiService.getProjectStructureAllowedChildTypesForElement(
          this.projectIdNumeric,
          this.elementIdNumeric,
          this.loggedInUser.entra_id,
          this.structureSetIdForRequests
        );
        this.rootAllowedChildTypes = this.normalizeCollection(response, 'types');
      } catch (error) {
        this.rootAllowedChildTypes = [];
      }
    },

    async fetchChildElements() {
      try {
        const response = await apiService.getProjectStructureElements(
          this.projectIdNumeric,
          this.loggedInUser.entra_id,
          this.elementIdNumeric,
          this.structureSetIdForRequests
        );
        const items = this.normalizeCollection(response, 'elements');
        this.childElements = items.map((item) => {
          const typeObj = item.type_id != null ? this.structureTypesById[item.type_id] : null;
          const typeByCode = item.type_code ? this.structureTypesByCode[item.type_code] : null;
          return {
            ...item,
            type_name: item.type_name || item.type?.name || item.structure_type_name || typeObj?.name || typeByCode?.name || '-',
            parent_name: item.parent_name || item.parent?.name || this.element?.name || 'Project',
          };
        });
      } catch (error) {
        this.childElements = [];
        this.toast.error('Failed to fetch child elements.');
      }
    },

    toggleEditMode() {
      this.isEditMode = true;
      this.editableElement = {
        name: this.element?.name || '',
        location_id: this.element?.location_id != null ? Number(this.element.location_id) : '',
        status: this.element?.status || 'To be Started',
      };
    },

    cancelEditMode() {
      this.isEditMode = false;
    },

    async saveChanges() {
      if (!this.editableElement.name) {
        this.toast.error('Name is required.');
        return;
      }
      if (this.requiresLocation && !this.editableElement.location_id) {
        this.toast.error('Location is required for this type.');
        return;
      }

      try {
        const payload = { name: this.editableElement.name, status: this.editableElement.status };
        if (this.requiresLocation) {
          payload.location_id = Number(this.editableElement.location_id);
        }

        await apiService.updateProjectStructureElement(
          this.elementIdNumeric,
          payload,
          this.loggedInUser.entra_id,
          this.structureSetIdForRequests
        );

        this.toast.success(`${this.typeName} updated.`);
        this.isEditMode = false;
        await this.fetchElement();
        if (this.requiresLocation) {
          await this.fetchLocationName();
        }
      } catch (error) {
        this.toast.error(error?.response?.data?.message || 'Failed to update element.');
      }
    },

    async deleteElement() {
      try {
        const isConfirmed = await confirmDelete(`Are you sure you want to delete this ${this.typeName}?`);
        if (!isConfirmed) return;

        await apiService.deleteProjectStructureElement(
          this.elementIdNumeric,
          this.loggedInUser.entra_id,
          this.structureSetIdForRequests
        );

        this.$router.push({ name: 'ProjectDetailPage', params: { id: this.projectIdNumeric } });
      } catch (error) {
        this.toast.error(error?.response?.data?.message || 'Failed to delete element.');
      }
    },

    async fetchLocations() {
      try {
        const response = await apiService.get(API_ENDPOINTS.locations, {}, this.loggedInUser.entra_id);
        this.locations = this.normalizeCollection(response, 'locations');
      } catch (error) {
        this.locations = [];
      }
    },

    async fetchLocationName() {
      if (!this.element?.location_id) {
        this.locationName = '';
        return;
      }
      try {
        const response = await apiService.get(API_ENDPOINTS.location(this.element.location_id), {}, this.loggedInUser.entra_id);
        this.locationName = response?.name || '';
      } catch (error) {
        this.locationName = '';
      }
    },

    async fetchComments() {
      try {
        const response = await axios.get(API_ENDPOINTS.projectStructureElementComments(this.elementIdNumeric), {
          params: {
            entra_id: this.loggedInUser.entra_id,
          },
        });
        this.comments = response.data;
        console.log('Fetched comments:', this.comments);
      } catch (error) {
        if (error?.response?.status === 404) {
          this.comments = [];
          return;
        }
        this.toast.error('Failed to fetch comments.');
      }
    },

    async fetchUsers() {
      try {
        const response = await axios.get(API_ENDPOINTS.users, { params: { entra_id: this.loggedInUser.entra_id } });
        this.users = response.data;
      } catch (error) {
        this.users = [];
      }
    },

    async addComment(newComment, authorId) {
      try {
        const text = typeof newComment === 'string'
          ? newComment.trim()
          : (newComment?.text || '').trim();
        const resolvedAuthorId = authorId ?? this.loggedInUser?.id;

        if (!text || !resolvedAuthorId) {
          this.toast.error('Failed to add comment.');
          return;
        }

        const payload = {
          text,
          author_id: resolvedAuthorId,
        };

        await apiService.createResource(
          API_ENDPOINTS.projectStructureElementComments(this.elementIdNumeric),
          payload,
          this.loggedInUser.entra_id
        );
        await this.fetchComments();
      } catch (error) {
        this.toast.error('Failed to add comment.');
      }
    },

    async removeComment(commentId) {
      try {
        await apiService.deleteResource(API_ENDPOINTS.projectStructureComment(commentId), this.loggedInUser.id, this.loggedInUser.entra_id);
        this.comments = this.comments.filter((comment) => comment.id !== commentId);
      } catch (error) {
        this.toast.error('Failed to remove comment.');
      }
    },

    async handleUpdateComment(commentId, text) {
      try {
        const payload = {
          text,
          user_id: this.loggedInUser.id,
          entra_id: this.loggedInUser.entra_id,
        };

        await axios.patch(
          API_ENDPOINTS.projectStructureComment(commentId),
          payload,
          {
            headers: { 'Content-Type': 'application/json' },
          }
        );
        await this.fetchComments();
      } catch (error) {
        this.toast.error('Failed to update comment.');
      }
    },

    async fetchExperiments() {
      this.experiments = [];
      try {
        const response = await apiService.getExperimentsFromStructureElement(this.elementIdNumeric, this.loggedInUser.entra_id);
        this.experiments = this.normalizeCollection(response, 'experiments');
      } catch (error) {
        this.experiments = [];
      }
    },

    updateNewExperimentField(field, value) {
      this.newExperiment = {
        ...this.newExperiment,
        [field]: value,
      };
    },

    async openCreateExperimentModal() {
      if (this.isCreatingExperiment || !this.canHaveExperiments) return;
      if (!this.locations.length) {
        await this.fetchLocations();
      }

      this.newExperiment = buildDefaultExperiment(this.projectIdNumeric, this.elementIdNumeric);
      if (this.element?.location_id) {
        this.newExperiment.location_id = Number(this.element.location_id);
      }
      this.createExperimentErrors = [];
      this.showCreateExperimentModal = true;
    },

    closeCreateExperimentModal() {
      if (this.isCreatingExperiment) return;
      this.showCreateExperimentModal = false;
      this.createExperimentErrors = [];
    },

    validateNewExperiment() {
      this.createExperimentErrors = [];
      if (!this.newExperiment.name?.trim()) this.createExperimentErrors.push('name');
      if (!this.newExperiment.location_id) this.createExperimentErrors.push('location_id');
      return this.createExperimentErrors.length === 0;
    },

    async submitCreateExperiment() {
      if (this.isCreatingExperiment) return;
      if (!this.validateNewExperiment()) {
        this.toast.error('Please fill out all required experiment fields.');
        return;
      }

      this.isCreatingExperiment = true;
      try {
        await apiService.createResource(
          API_ENDPOINTS.experiments,
          {
            name: this.newExperiment.name.trim(),
            location_id: Number(this.newExperiment.location_id),
            project_structure_element_id: Number(this.elementIdNumeric),
            project_id: Number(this.projectIdNumeric),
            user_id: this.loggedInUser.id,
            ...(this.newExperiment.start_date ? { start_date: this.newExperiment.start_date } : {}),
            ...(this.newExperiment.end_date ? { end_date: this.newExperiment.end_date } : {}),
          },
          this.loggedInUser?.entra_id
        );

        this.toast.success('Experiment created successfully!');
        this.showCreateExperimentModal = false;
        await this.fetchExperiments();
      } catch (error) {
        console.error('Failed to create experiment from structure element detail page:', error);
        this.toast.error('Failed to create experiment.');
      } finally {
        this.isCreatingExperiment = false;
      }
    },

    async fetchFiles() {
      this.isFilesTreeLoading = true;
      try {
        const response = await axios.get(API_ENDPOINTS.filesTree, {
          params: {
            file_type: 'structure_element',
            entity_id: this.elementIdNumeric,
            entra_id: this.loggedInUser.entra_id,
          },
        });
        const { files, folders, basePath } = parseFilesTree(response.data, {
          folderExtras: { associative_type: 'structure_element', associative_id: this.elementIdNumeric },
        });
        this.files = files;
        this.folders = folders;
        this.filesBasePath = basePath;
      } catch (error) {
        this.files = [];
        this.folders = [];
      } finally {
        this.isFilesTreeLoading = false;
      }
    },

    async addFiles(payload = {}) {
      const incomingFiles = payload.files || [];
      const folderId = payload.folderId ?? null;
      if (!incomingFiles.length || this.isUploading) {
        return;
      }

      this.uploadSession = {
        visible: true,
        fileType: 'structure_element',
        entityId: this.elementIdNumeric,
        files: Array.from(incomingFiles),
        folderId,
      };
      this.isUploading = true;
      this.uploadPercent = 0;
      this.uploadLabel = `Uploading 0 / ${incomingFiles.length} files...`;
    },

    async removeFile(fileId) {
      try {
        await apiService.deleteResource(API_ENDPOINTS.file(fileId), this.loggedInUser.id, this.loggedInUser.entra_id);
        this.files = this.files.filter((file) => file.id !== fileId);
      } catch (error) {
        this.toast.error('Failed to delete file.');
      }
    },

    async handleUploadComplete() {
      this.isUploading = false;
      this.uploadPercent = 100;
      this.uploadLabel = 'Upload complete';
      this.uploadSession.visible = false;
      await this.fetchFiles();
    },

    handleUploadClose() {
      this.uploadSession.visible = false;
      this.isUploading = false;
      this.uploadPercent = 0;
      this.uploadLabel = '';
    },

    handleUploadError(errorMessage) {
      this.uploadSession.visible = false;
      this.isUploading = false;
      this.uploadPercent = 0;
      this.uploadLabel = '';
      this.toast.error(errorMessage || 'Upload failed.');
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/main.scss";

.element-details {
  max-width: 800px;
  margin: 0 auto;
  padding: 15px 20px;
  border-radius: 8px;
  background-color: $secondary-color;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    h2 {
      color: $primary-color;
      margin-bottom: 20px;
    }
  }

  .top-buttons {
    display: flex;
    gap: 10px;
  }

  .detail {
    margin-bottom: 6px;
    font-size: 16px;

    strong {
      color: $primary-color;
    }

    .linked-value {
      color: $primary-color;
      text-decoration: none;
      font-weight: 600;
      margin-left: 4px;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .buttons-container {
    display: flex;
    justify-content: space-between;
    margin-top: 12px;
  }
}

.entity-path {
  font-size: .95rem;
  margin: 4px 0 12px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f6f7fb;
  display: inline-block;
}

.entity-path__label {
  opacity: .8;
  margin-right: 4px;
}

.entity-path__sep {
  margin: 0 6px;
  opacity: .6;
}

.entity-path__loading,
.entity-path__error {
  opacity: .85;
}
</style>